import { isFulfilled } from "@reduxjs/toolkit";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import {
    DEFAULT_ITEMS_PER_PAGE,
    DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY,
    DEFAULT_SEARCH_INPUT_LENGTH,
    ITEMS_PER_PAGE_OPTIONS,
    MAX_VISIBLE_PAGES,
    USER_ROLES,
} from "../../../../constants/app";
import useDebouncedValue from "../../../../hooks/useDebouncedValue";
import Roles from "../../../../models/roles";
import { AUTH_ROUTE } from "../../../../pages/App/routes";
import {
    useAppDispatch as useDispatch,
    useAppSelector as useSelector,
} from "../../../../store";
import {
    hideTransparentPreloaderAction,
    showNotificationAction,
    showTransparentPreloaderAction,
} from "../../../../store/application/slice";
import {
    getAllCenters,
    getAllCentreUsers,
} from "../../../../store/centers/thunks";
import {
    TAllCentersData,
    TAllCentersList,
    TCenterDetails,
} from "../../../../store/centers/types";
import { otherRequestsThunk } from "../../../../store/superadmin/thunks";
import { capitalizeName } from "../../../../utils/stringUtils";
import Pagination from "../../../Pagination/Pagination";
import Users from "../../../PopupModal/Users";
import SaveNotification from "../../../SaveNotification";
import CustomButton from "../../../UI/Buttons/CustomButton";
import Block from "../../../UI/Icons/Block";
import ChevronSmall from "../../../UI/Icons/ChevronSmall";
import EditPencil from "../../../UI/Icons/EditPencil";
import Mail from "../../../UI/Icons/Mail";
import RoundedPlus from "../../../UI/Icons/RoundedPlus";
import RoundedTick from "../../../UI/Icons/RoundedTick";
import Search from "../../../UI/Icons/Search";
import VerticalDots from "../../../UI/Icons/VerticalDots";

const CenterUsers: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement | null>(null); // Ref to the dropdown menu for detecting click outside

  const user = useSelector((state) => state.auth.user);
  const allCentersList = useSelector((state) => state.Centres.allCenters);
  const centerUserList = useSelector(
    (state: { Centres: { allCentreUsers: any } }) =>
      state.Centres.allCentreUsers
  );

  const isInitialLoad = useRef<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [paginationLinks, setPaginationLinks] = useState<any[]>([]);
  const [editingUser, setEditingUser] = useState<number>(0);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedCentre, setSelectedCentre] = useState<number[]>([]);
  const [selectedRole, setSelectedRole] = useState<number[]>([]);
  const [selectedKeyword, setSelectedKeyword] = useState<string>("");
  const [itemPerPageList, setItemPerPageList] = useState<number>(
    DEFAULT_ITEMS_PER_PAGE
  );
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const [openDropdownForEdit, setOpenDropdownForEdit] =
    useState<boolean>(false);
  const [editValues, setEditValues] = useState<any>(null);
  const [editUserId, setEditUserId] = useState<number | null>(0);
  const [searchInput, setSearchInput] = useState("");

  const isCentreAdmin = useMemo(
    () => user?.role.name === Roles.CENTER_ADMIN,
    [user]
  );

  /**
   * Fetches a list of users based on the provided parameters.
   *
   * @param page - The current page number for pagination.
   * @param keyword - A search keyword to filter the users.
   * @param centre - An array of centre IDs to filter the users by their associated centres.
   * @param role - An array of role IDs to filter the users by their roles.
   * @returns A promise that dispatches an action to fetch the users.
   */
  const fetchUsers = async (
    page: number,
    keyword: string,
    centre: Array<number | null>,
    role: Array<number | null>
  ) => {
    dispatch(showTransparentPreloaderAction());
    await dispatch(
      getAllCentreUsers({
        page,
        keyword,
        centre_id: selectedCentre,
        role_id: selectedRole,
        per_page: itemPerPageList,
      })
    );
    await dispatch(getAllCenters());
    dispatch(hideTransparentPreloaderAction());
  };

  /**
   * Handles the change in the number of items per page by updating the `itemPerPageList` state.
   *
   * @param value - The numeric value representing the number of items per page.
   */
  const handlePageChange = (url: string | null) => {
    if (url) {
      isInitialLoad.current = false;
      const page = new URL(url).searchParams.get("page");
      setCurrentPage(Number(page));
    }
  };

  /**
   * Handles the creation of a new user by resetting the form fields and opening the modal.
   */
  const handleCreateUser = () => {
    setShowModal(!false);
  };

  /**
   * Handles the editing of a user by populating form fields with the selected user's data
   * and opening the modal for editing.
   *
   * - Opens the modal for editing the user.
   */
  const handleEditUser = (user: TAllCentersData) => {
    setOpenDropdownForEdit(false);
    setEditingUser(user?.id);
    setEditValues(user);
    // Open the modal
    setShowModal(true);
  };

  /**
   * Handles changes to the centre filter selection.
   *
   * @param value - The numeric value representing the selected centre.
   *                A value of 0 indicates no selection.
   *
   * - If the value is not 0 and does not already exist in the `selectedCentre` array:
   *   - Sets `isInitialLoad.current` to `false`.
   *   - Updates the `selectedCentre` state by appending the new value to the existing array.
   */
  const handleCentreFilterChange = (value: number) => {
    // let selectedCentre: Array<number | null> = [];
    if (value !== 0) {
      // Check if the value already exists in the array
      if (!selectedCentre.includes(value)) {
        isInitialLoad.current = false;
        setSelectedCentre((prevValue: Array<number>) => {
          return [...prevValue, value];
        });
      }
    }
  };

  /**
   * Handles changes to the role filter by adding the selected role value to the list of selected roles
   * if it is not already present and is not equal to 0.
   *
   * @param value - The numeric value of the selected role filter.
   *                A value of 0 is ignored and not added to the selected roles.
   *
   * @remarks
   * - If the value is not already in the `selectedRole` array, it is added to the array.
   * - Sets `isInitialLoad.current` to `false` to indicate that the initial load state has changed.
   * - Updates the `selectedRole` state with the new value.
   */
  const handleRoleFilterChange = (value: number) => {
    if (value !== 0) {
      setCurrentPage(1);
      // Check if the value already exists in the array
      if (!selectedRole.includes(value)) {
        isInitialLoad.current = false;
        setSelectedRole((prevValue: Array<number>) => {
          return [...prevValue, value];
        });
      }
    }
  };

  // Function to get comma-separated business names
  const getBusinessNames = (centres: TCenterDetails[]): string => {
    return centres.map((centre) => centre.business_name).join(", ");
  };

  /**
   * Toggles the state of a dropdown menu based on the provided index.
   * If the dropdown corresponding to the given index is already open, it will be closed.
   * Otherwise, it will open the dropdown for the specified index and close any other open dropdowns.
   *
   * @param index - The index of the dropdown to toggle.
   */
  const toggleDropdown = (index: number) => {
    setOpenDropdown((prevIndex) => (prevIndex === index ? null : index));
  };

  useEffect(() => {
    // if(!isInitialLoad?.current){
    fetchUsers(currentPage, selectedKeyword, selectedCentre, selectedRole);
    //   isInitialLoad.current = true;
    // }
  }, [
    currentPage,
    selectedKeyword,
    selectedCentre,
    selectedRole,
    itemPerPageList,
  ]);

  /**
   * Converts an array of centre IDs to their corresponding business names.
   *
   * @param ids - An array of numeric centre IDs.
   * @param centres - An array of centre objects containing their details.
   * @returns A string of comma-separated business names for the given centre IDs.
   */
  const convertIdsToBusinessNames = (
    ids: number[],
    centers: TAllCentersList[]
  ): string => {
    return ids
      .map((id) => centers.find((center) => center.id === id)?.business_name)
      .filter(Boolean)
      .join(", ");
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const filterMenu = document.querySelector(".filter-menu");
      if (
        filterMenu &&
        !filterMenu.contains(event.target as Node) &&
        openDropdown !== null
      ) {
        toggleDropdown(0); // Close the dropdown
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdown, openDropdown === 1, openDropdown === 2]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutsideEditBtn = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenDropdownForEdit(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutsideEditBtn);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideEditBtn);
    };
  }, []);

  const redirectToEnquiry = () => {
    navigate(AUTH_ROUTE.CLIENT_ENQUIRY);
  };

  /**
   * Refreshes the user list by fetching the latest data.
   */
  const refreshUserList = () => {
    fetchUsers(currentPage, selectedKeyword, selectedCentre, selectedRole);
  };

  /**
   * Updates the current page and items per page when the component mounts or when the `candidatesList` changes.
   */
  useEffect(() => {
    if (centerUserList) {
      setTotalPages(centerUserList?.last_page || 1);
      setPaginationLinks(centerUserList?.links);
    }
  }, [centerUserList]);

  const makeUserUpdate = async (id: string, action: string) => {
    const payload = {
      user_uuid: id,
      action: action,
    };
    setOpenDropdownForEdit(false);
    dispatch(showTransparentPreloaderAction());
    const res = await dispatch(otherRequestsThunk(payload));
    if (isFulfilled(res)) {
      dispatch(hideTransparentPreloaderAction());
      await refreshUserList();
      if (action === "resend_invite") {
        dispatch(showNotificationAction("Invite Resent"));
      } else if (action === "cancel_invite") {
        dispatch(showNotificationAction("Invite Cancelled"));
      } else if (action === "make_active") {
        dispatch(showNotificationAction("User Activated"));
      } else if (action === "make_inactive") {
        dispatch(showNotificationAction("User Inactivated"));
      }
    }
  };

  /**
   * Debounces the `selectedKeyword` state to avoid excessive API calls while the user is typing.
   * The debounced value is used to trigger the API call for fetching candidates.
   */
  const debouncedSearch = useDebouncedValue(
    searchInput,
    DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY
  );

  /**
   * Handles the change in the search input by updating the `selectedKeyword` state.
   * It also resets the current page to 1 if the search input is empty or exceeds the default length.
   *
   */
  useEffect(() => {
    if (
      debouncedSearch.length === 0 ||
      debouncedSearch.length > DEFAULT_SEARCH_INPUT_LENGTH
    ) {
      setSelectedKeyword(debouncedSearch); // Update the selected keyword state
      setCurrentPage(1); // Reset the current page to 1
    }
  }, [debouncedSearch]);

  return (
    <>
      {showNotification && (
        <SaveNotification
          message={editingUser === 0 ? "Invite Sent" : "Saved"}
          onClose={() => setShowNotification(false)}
        />
      )}

      {/** Popup for Add/Edit Centre user */}
      <Users
        editingUser={editingUser}
        showModal={showModal}
        setShowModal={setShowModal}
        setShowNotification={setShowNotification}
        user={"client"}
        editValues={editValues}
        refreshUserList={refreshUserList}
        setEditValues={setEditValues}
        setEditingUser={setEditingUser}
      />
      {/** End - Popup for Add/Edit Centre user */}

      <div className="d-flex flex-column flex-lg-row gap-50 gap-mobile-20">
        <div className="d-flex flex-column gap-20 min-w-176px w-176px mobile-w-100 text-break">
          <div className="fs-16 fw-700 text-black ">
            {centerUserList?.length} Centre Users{" "}
          </div>
          <div className="w-100 h-1px bg-off-white"></div>
          <div className="d-flex gap-10 align-items-center justify-content-between">
            <div className="fs-16 fw-700 text-teal-dark cursor-pointer">
              {allCentersList?.length} Centres
            </div>
            {isCentreAdmin && (
              <div onClick={redirectToEnquiry}>
                <RoundedPlus
                  className="min-w-24px cursor-pointer"
                  width={24}
                  height={24}
                  fill="#167E76"
                />
              </div>
            )}
          </div>
          {allCentersList.map((centreData: TAllCentersList) => (
            <div
              key={centreData.id}
              className={`fs-16 text-black cursor-pointer `}
              onClick={() => {
                localStorage.setItem(
                  "centre_id",
                  JSON.stringify(centreData.id)
                );
                window.location.href = AUTH_ROUTE.CLIENT_CENTRES;
              }}
            >
              {centreData.business_name}
            </div>
          ))}
        </div>
        <div className="w-100 h-1px bg-off-white d-block d-lg-none"></div>
        <div className="border-left border-off-white pl-50px w-100 mobile-p-0 mobile-border-0">
          <div className="d-flex flex-column gap-20">
            <div className="fs-30 fw-400 text-black">Centre Users</div>
            <div className="d-flex flex-column flex-lg-row gap-20 align-items-lg-center justify-content-lg-between">
              <div className="d-flex flex-column flex-lg-row gap-20 align-items-lg-center">
                <div className="form-group w-200px mobile-w-100 input-icon icon-left">
                  <Search />
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search..."
                    // onChange={(e) => {
                    //   const keyword = e.target.value.trim();
                    //   setSelectedKeyword(keyword?.length > 3 ? keyword : "");
                    //   if (keyword?.length > 3 || keyword?.length === 0) {
                    //     isInitialLoad.current = false;
                    //   }
                    // }}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </div>
                <div className="position-relative">
                  <div
                    className="d-flex gap-3 align-items-center cursor-pointer"
                    onClick={() => toggleDropdown(1)}
                  >
                    <div className="fs-13 fw-400 text-black">Centres</div>
                    <div className="fs-13 fw-700 text-black">
                      {/* {selectedCentre?.length > 0
                        ? convertIdsToBusinessNames(
                            selectedCentre,
                            allCentersList
                          )
                        : "All"} */}
                      {selectedCentre?.length > 0
                        ? selectedCentre?.length + " Centres selected"
                        : "All"}
                    </div>

                    <ChevronSmall isOpen={openDropdown === 1 ? true : false} />
                  </div>
                  {openDropdown === 1 && (
                    <div className="position-absolute end-0 d-flex flex-column dropdown-menu w-266px">
                      <div className="filter-menu">
                        {allCentersList.length > 0 ? (
                          allCentersList?.map((center: TAllCentersList) => {
                            return (
                              <div
                                className="theme-check dropdown-item"
                                key={center.id}
                              >
                                <input
                                  type="checkbox"
                                  className="theme-check-input"
                                  id={`checkbox_${center.id}`}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      handleCentreFilterChange(center.id);
                                    } else {
                                      isInitialLoad.current = false;
                                      setSelectedCentre((prevValue) =>
                                        prevValue.filter(
                                          (id) => id !== center.id
                                        )
                                      );
                                    }
                                    toggleDropdown(1);
                                  }}
                                  checked={selectedCentre.includes(center.id)}
                                />
                                <label
                                  htmlFor={`checkbox_${center.id}`}
                                  className="theme-check-label fs-16 text-truncate"
                                >
                                  {center.business_name}
                                </label>
                              </div>
                            );
                          })
                        ) : (
                          <div className="d-flex flex-column align-items-center theme-check dropdown-item">
                            No Centres Found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="position-relative">
                  <div
                    className="d-flex gap-3 align-items-center cursor-pointer"
                    onClick={() => toggleDropdown(2)}
                  >
                    <div className="fs-13 fw-400 text-black">User Types</div>
                    <div className="fs-13 fw-700 text-black">
                      {selectedRole?.length > 0
                        ? // ? selectedRole.join(",")
                          selectedRole?.length + " items selected"
                        : "All"}
                    </div>
                    <ChevronSmall isOpen={openDropdown === 2 ? true : false} />
                  </div>
                  {openDropdown === 2 && (
                    <div className="position-absolute end-0 d-flex flex-column dropdown-menu w-266px filter-menu">
                      <div className="theme-check dropdown-item">
                        <input
                          type="checkbox"
                          className="theme-check-input"
                          id="checkbox_2_1"
                          onChange={(e) => {
                            if (e.target.checked) {
                              handleRoleFilterChange(USER_ROLES.CENTRE_ADMIN);
                            } else {
                              isInitialLoad.current = false;
                              setSelectedRole((prevValue) =>
                                prevValue.filter(
                                  (id) => id !== USER_ROLES.CENTRE_ADMIN
                                )
                              );
                            }
                            toggleDropdown(2);
                          }}
                          checked={selectedRole.includes(
                            USER_ROLES.CENTRE_ADMIN
                          )}
                        />
                        <label
                          htmlFor="checkbox_2_1"
                          className="theme-check-label fs-16 text-truncate"
                        >
                          Centre Admin
                        </label>
                      </div>
                      <div className="theme-check dropdown-item">
                        <input
                          type="checkbox"
                          className="theme-check-input"
                          id="checkbox_2_2"
                          onChange={(e) => {
                            if (e.target.checked) {
                              handleRoleFilterChange(USER_ROLES.CENTRE_USER);
                            } else {
                              isInitialLoad.current = false;
                              setSelectedRole((prevValue) =>
                                prevValue.filter(
                                  (id) => id !== USER_ROLES.CENTRE_USER
                                )
                              );
                            }
                            toggleDropdown(2);
                          }}
                          checked={selectedRole.includes(
                            USER_ROLES.CENTRE_USER
                          )}
                        />
                        <label
                          htmlFor="checkbox_2_2"
                          className="theme-check-label fs-16 text-truncate"
                        >
                          Centre Users
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {isCentreAdmin && (
                <CustomButton
                  variant={"primary"}
                  type="button"
                  className="btn-theme bg-teal text-black"
                  onClick={handleCreateUser}
                >
                  <RoundedPlus width={20} height={20} fill="black" />
                  Create
                </CustomButton>
              )}
            </div>
            {/* Client Viewing Staff Row */}
            <div className="d-flex flex-column">
              <div className="w-100 h-1px bg-off-white"></div>
              {centerUserList?.data?.length > 0 ? (
                centerUserList?.data?.map(
                  (user: TAllCentersData, index: number) => {
                    return (
                      <>
                        <div
                          key={index}
                          className="d-flex flex-column flex-lg-row gap-20 align-items-center justify-content-between p-15px border-bottom border-off-white text-break"
                        >
                          <div className="d-flex flex-column gap-3 min-w-300px w-300px mobile-w-100">
                            <div
                              className={`fs-16 fw-700 ${
                                user?.status === "active"
                                  ? "text-black"
                                  : "text-grey-light"
                              }`}
                            >
                              {capitalizeName(user?.first_name ?? "")} {capitalizeName(user?.last_name ?? "")}
                              <span className="text-teal-dark fs-13">
                                {!user?.email_verified_at &&
                                  user.status !== "inactive" &&
                                  "(Invitation Pending)"}
                              </span>
                              <span className="text-grey-light fs-13">
                                {user.status === "inactive" && "(Inactive)"}
                              </span>
                            </div>
                            <div
                              className={`fs-13 fw-400 text-truncate ${
                                user?.status === "active"
                                  ? "text-black"
                                  : "text-grey-light"
                              }`}
                              title={user?.email}
                            >
                              {user?.email}
                            </div>
                            <div
                              className={`fs-13 fw-400 text-truncate ${
                                user?.status === "active"
                                  ? "text-black"
                                  : "text-grey-light"
                              }`}
                            >
                              {user?.phone_number}
                            </div>
                          </div>
                          <div className="d-flex gap-20 align-items-center justify-content-between w-100">
                            <div className="d-flex flex-column gap-3">
                              {user.status !== "inactive" ? (
                                <>
                                  <div className="fs-10 fw-400 text-grey-medium">
                                    Assigned to {user?.centres?.length}{" "}
                                    Businesses
                                  </div>
                                  <div className="fs-13 fw-400 text-black">
                                    {getBusinessNames(user?.centres)}
                                  </div>{" "}
                                </>
                              ) : (
                                ""
                              )}
                            </div>
                            <div className="position-relative">
                              {isCentreAdmin && (
                                <CustomButton
                                  variant={"primary"}
                                  className="btn-theme bg-white p-0 h-auto"
                                  onClick={() => {
                                    setOpenDropdownForEdit(
                                      !openDropdownForEdit
                                    );
                                    setEditUserId(user.id);
                                  }}
                                >
                                  <VerticalDots
                                    color={
                                      openDropdownForEdit &&
                                      editUserId === user?.id
                                        ? "#000000"
                                        : "#aaaaaa"
                                    }
                                  />
                                </CustomButton>
                              )}
                              {openDropdownForEdit &&
                                editUserId === user?.id && (
                                  <div
                                    className="position-absolute end-0 d-flex flex-column dropdown-menu"
                                    ref={dropdownRef}
                                  >
                                    {user.email_verified_at !== null &&
                                    user.status === "active" ? (
                                      <>
                                        <div
                                          className="dropdown-item cursor-pointer"
                                          onClick={() => handleEditUser(user)}
                                        >
                                          <EditPencil />
                                          Edit User
                                        </div>
                                        <div
                                          className="dropdown-item cursor-pointer text-nowrap"
                                          onClick={() =>
                                            makeUserUpdate(
                                              user?.uuid,
                                              "make_inactive"
                                            )
                                          }
                                        >
                                          <Block />
                                          Make Inactive
                                        </div>
                                      </>
                                    ) : user.email_verified_at === null &&
                                      user.status === "active" ? (
                                      <>
                                        <div
                                          className="dropdown-item cursor-pointer text-nowrap"
                                          onClick={() =>
                                            makeUserUpdate(
                                              user?.uuid,
                                              "resend_invite"
                                            )
                                          }
                                        >
                                          <Mail />
                                          Resend Invite
                                        </div>
                                        <div
                                          className="dropdown-item cursor-pointer text-nowrap"
                                          onClick={() =>
                                            makeUserUpdate(
                                              user?.uuid,
                                              "cancel_invite"
                                            )
                                          }
                                        >
                                          <Block />
                                          Cancel Invite
                                        </div>
                                      </>
                                    ) : user.status === "inactive" ? (
                                      <div
                                        className="dropdown-item cursor-pointer"
                                        onClick={() =>
                                          makeUserUpdate(
                                            user?.uuid,
                                            "make_active"
                                          )
                                        }
                                      >
                                        <RoundedTick />
                                        Make Active
                                      </div>
                                    ) : (
                                      ""
                                    )}
                                  </div>
                                )}
                            </div>
                          </div>
                        </div>
                      </>
                    );
                  }
                )
              ) : (
                <div className="d-flex gap-20 align-items-center justify-content-center text-center p-15px">
                  <div className="fs-16 fw-400 text-grey-medium">
                    No users found
                  </div>
                </div>
              )}
            </div>
            {/* Client Viewing Staff Row */}

            {/* Pagination Component */}
            {centerUserList?.total > DEFAULT_ITEMS_PER_PAGE && (
              <div className="d-flex gap-5 align-items-center justify-content-end flex-wrap">
                <div className="d-flex gap-3 align-items-center">
                  <div className="fs-13 text-black text-nowrap">
                    Items per page
                  </div>
                  <div className="form-group">
                    <select
                      name=""
                      id=""
                      className="form-select text-only"
                      onChange={(e) => {
                        isInitialLoad.current = false;
                        setItemPerPageList(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      {ITEMS_PER_PAGE_OPTIONS?.map((item, index) => {
                        return (
                          <option key={index} value={item}>
                            {item}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  paginationLinks={paginationLinks}
                  maxVisiblePages={MAX_VISIBLE_PAGES}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
            {/* Pagination Component */}
          </div>
        </div>
      </div>
    </>
  );
};

export default CenterUsers;
